import { Globe } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@wellcheck/components/ui/select";

import { buildLocaleSwitchPath } from "./buildLocaleSwitchPath";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  type SupportedLocale,
} from "./config";

/**
 * LanguageSelector — ヘッダー右側に置く言語切替セレクタ。
 *
 * URL の :lang セグメントを差し替えて navigate するため、URL が言語決定の一次ソースで
 * あり続ける。i18n.changeLanguage は LanguageRoute 側で URL に追従して呼ばれる。
 *
 * 「言語切替である」ことが一目でわかるよう、地球儀アイコン (lucide-react Globe) を
 * trigger 内に常時表示する。選択肢・現在値はネイティブ表記 (日本語/English/中文/...)
 * で表示し、UI 言語に依存せず自分の母語を見つけられるようにする。
 *
 * trigger 幅は `w-[...]` で固定する (min-w ではなく)。可変幅にすると選択言語ごとに
 * ラベル長が変わり (最長は "Tiếng Việt")、ヘッダー内の他要素 (サービス名リンク等) の
 * 位置がガタつくため、5 言語中最長のラベルを基準に十分な幅を確保した固定値にする。
 */
export function LanguageSelector() {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const current: SupportedLocale = isSupportedLocale(lang) ? lang : DEFAULT_LOCALE;

  const onChange = useCallback(
    (next: string) => {
      if (!isSupportedLocale(next) || next === current) return;
      const target = buildLocaleSwitchPath(location.pathname, location.search, location.hash, next);
      navigate(target, { replace: true });
    },
    [current, location, navigate],
  );

  return (
    <Select value={current} onValueChange={onChange}>
      <SelectTrigger size="sm" aria-label={t("language.label")} className="w-[9.5rem]">
        <Globe aria-hidden="true" className="size-4 shrink-0" />
        <SelectValue className="truncate" />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LOCALES.map((locale) => (
          <SelectItem key={locale} value={locale} lang={locale}>
            {t(`language.${locale}` as const)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
